// =================================================
// IMPORTS
// -------------------------------------------------
// React and MUI Components
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
// -------------------------------------------------
// Contexts
import { useAuth } from "../contexts/auth";
import { fetchSurveyById } from "../redux/reducers/surveys";
// Redux selectors
import { taskResponsesSelectors } from "../redux/reducers/taskResponses";
// -------------------------------------------------
// Custom Components
import DashboardLineChart from "./dashboard-cbti/Dashboard_LineChart";
import SleepBarChart from "./dashboard-cbti/SleepBarChart";
import SurveyResults from "./dashboard-cbti/SurveyResults";
// -------------------------------------------------
// Basic elements
import { Button, Card, CardContent, Chip, Typography } from "@mui/material";
import { ChevronLeft } from "@mui/icons-material";
// =================================================
// COMPONENT
// -------------------------------------------------
const DashboardCBTIContent = React.memo(({ participantData, currentRole }) => {
  const { t } = useTranslation("components", {
    keyPrefix: "DashboardCBTIContent",
  });
  // ===============================================
  // VARIABLES
  // -----------------------------------------------
  // Contexts
  const { currentAuth } = useAuth();
  // -----------------------------------------------
  // Redux
  const dispatch = useDispatch();
  const isXS = useSelector((state) => state.ui.isXS);
  const isSM = useSelector((state) => state.ui.isSM);
  const surveyEntities = useSelector((state) => state.surveys.entities);
  const taskResponseList = useSelector((state) =>
    taskResponsesSelectors.selectAll(state),
  );
  // Use optional chaining to safely access participantData properties
  const cbtiTickets = participantData?.cbtiTickets || [];

  // Collect all required survey IDs
  const surveyIdsNeeded = cbtiTickets
    .map((ticket) => ticket.surveyId)
    .filter(Boolean);

  // Fetch surveys when cbtiTasks change
  useEffect(() => {
    surveyIdsNeeded.forEach((surveyId) => {
      // Survyes are sometimes partially loaded, one field that is missing is aliasList
      if (!surveyEntities[surveyId].aliasList) {
        dispatch(
          fetchSurveyById({
            requestingUser: currentAuth,
            surveyId,
            upsert: true,
          }),
        );
      }
    });
  }, [surveyIdsNeeded, dispatch, currentAuth, surveyEntities]);

  // Check if all surveys are loaded
  const allSurveysLoaded = surveyIdsNeeded.every((id) => surveyEntities[id]);

  // ===============================================
  // RENDER COMPONENT
  // -----------------------------------------------

  if (!allSurveysLoaded) {
    return <Typography>Loading surveys...</Typography>;
  }

  return (
    <div className="mb-2 pe-xs-0 pe-sm-0 pe-md-0 pe-lg-3 pe-xl-3">
      <div className="my-2">
        {currentRole === "participant" && !isXS && !isSM ? (
          <Typography variant="h2" className="my-2 d-inline">
            {t("Sleep overview")}
          </Typography>
        ) : currentRole !== "participant" ? (
          <>
            <Button
              startIcon={<ChevronLeft />}
              className="me-5"
              onClick={() => window.history.back()}
            >
              Back
            </Button>
            <Typography variant="h2" className="my-2 d-inline  me-2">
              {participantData.email}
            </Typography>
            <Chip
              label={participantData.userNumber}
              className="d-inline py-1"
            />
          </>
        ) : null}
      </div>
      <Card className="mb-2">
        <CardContent className="pb-0">
          <div className="row">
            <div className="col-xs-12 col-sm-12 col-md-12 col-lg-6 col-xl-6 p-0 mb-xs-3 mb-sm-3 mb-md-3 mb-lg-0 mb-xl-0">
              <SleepBarChart data={participantData} cbtiTickets={cbtiTickets} />
            </div>
            <div className="col-xs-12 col-sm-12 col-md-12 col-lg-6 col-xl-6">
              <div className="row">
                {[
                  {
                    data: participantData?.sleepEfficiency ?? [],
                    payload: participantData?.sleepLogTaskResponses ?? [],
                    alias: "CSD_SLP_EFF_TST_TIB",
                    title: "SE (%)",
                    options: {
                      ticks: [0, 0.8, 1],
                      unit: "%",
                      domain: [0, 1],
                    },
                  },
                  {
                    data: participantData?.totalSleepTime ?? [],
                    payload: participantData?.sleepLogTaskResponses ?? [],
                    alias: "CSD_TOTAL_SLEEP_TIME",
                    title: "TST (min)",
                    options: {
                      ticks: [120, 180, 240, 300, 360, 420, 480, 540, 600],
                      unit: "min",
                      domain: [120, 600],
                    },
                  },
                  {
                    data: participantData?.sleepOnsetLatency ?? [],
                    payload: participantData?.sleepLogTaskResponses ?? [],
                    alias: "CSD5",
                    title: "SOL (min)",
                    options: {
                      ticks: [5, 15, 30, 45, 60, 90, 120],
                      unit: "min",
                      domain: [-1, 120],
                    },
                  },
                  {
                    data: participantData?.wakeAfterSleepOnset ?? [],
                    payload: participantData?.sleepLogTaskResponses ?? [],
                    alias: "CSD7",
                    title: "WASO (min)",
                    options: {
                      ticks: [5, 15, 30, 45, 60, 90, 120],
                      unit: "min",
                      domain: [-1, 120],
                    },
                  },
                  {
                    data: participantData?.sleepQuality ?? [],
                    payload: participantData?.sleepLogTaskResponses ?? [],
                    alias: "CSD11",
                    title: "Sleep Quality",
                    options: {
                      ticks: [1, 2, 3, 4, 5],
                      unit: "",
                      domain: [0.5, 5.5],
                    },
                  },
                ].map((chart, index) => (
                  <div className="col-6 mb-2 p-0" key={index}>
                    <DashboardLineChart
                      data={chart.data}
                      payload={chart.payload}
                      alias={chart.alias}
                      title={chart.title}
                      options={chart.options}
                      cbtiTickets={cbtiTickets}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card className="mb-2">
        <CardContent>
          <Typography variant="h6">CBTI Responses</Typography>
          <SurveyResults
            cbtiTickets={[
              ...cbtiTickets.map((ticket) => {
                const taskResponse = taskResponseList.find(
                  (tr) => tr._id === ticket.responseId,
                );
                return { ...ticket, taskResponse };
              }),
            ]}
            surveyEntities={surveyEntities}
          />
        </CardContent>
      </Card>
    </div>
  );
});

// =================================================
// EXPORT COMPONENT
export default DashboardCBTIContent;
